import { NextPage } from "next";
import Head from "next/head";
import Script from "next/script";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Flights,
  Services,
  FlightsByDestination,
  NewsDetails,
  AirlineList,
  ModuleMatcher2,
} from "@/components";
import { IPageDTO, PAGE_CAT } from "@/interfaces/IPageDTO";
import { AIRLINES_LIST_PAGE_URLS } from "@/config/airlinesConfig";
import {
  FLIGHT_DETAIL_PAGE_URLS,
  FLIGHT_LIST_PAGE_URLS,
  MovementType,
} from "@/config/flightPages";
import { getAllPages, getFooterData, getNavigationData } from "@/api";
import getTargetUrl from "@/api/getTargetUrl";

// 👇 UI breadcrumb (Recoil)
import { useSetRecoilState } from "recoil";
import { CrumbsItemAtom, HideCrumbsAtom } from "@/atoms";
import PagePathAtom from "@/atoms/PagePathAtom";

/* ===================== SSR ===================== */
export async function getServerSideProps(ctx: any) {
  const { lang, paths } = ctx.params;

  const data = await getAllPages({ lang, paths });
  const footerData = await getFooterData();
  const navigationData = await getNavigationData();

  ctx.res.setHeader("Cache-Control", "public, max-age=120");

  const dataPassedToModuleMatcher = data.data?.find(
    (item: { xtaxAirports: { ti: any[] } }) =>
      item.xtaxAirports.ti.find(
        (airportItem) =>
          airportItem.label.toLowerCase() ===
          process.env.NEXT_PUBLIC_AIRPORT_REFERENCE?.toLowerCase()
      )
  );

  const excludes: string[] = [];

  if (!dataPassedToModuleMatcher) return { notFound: true };

  const dataPassToModuleMatcher = await Promise.all(
    dataPassedToModuleMatcher.lstModule
      ?.filter(
        (item: { xconModules: { linkedcontenttype: any } }) =>
          !excludes?.includes(item.xconModules.linkedcontenttype)
      )
      .map(
        async (moduleItem: {
          xconModules: { linkedcontenttype: any; targeturl: string };
          id: any;
        }) => {
          const moduleData = await getTargetUrl(moduleItem.xconModules.targeturl);
          return {
            linkedcontenttype: moduleItem.xconModules.linkedcontenttype,
            id: moduleItem.id,
            moduleData,
          };
        }
      )
  );

  return {
    props: { lang, data, footerData, navigationData, paths, dataPassToModuleMatcher },
  };
}

/* ===================== Tipi ===================== */
interface IPageProps {
  data: { data: IPageDTO[] };
  paths: string[];
  lang: string;
  navigationData: any;
  dataPassToModuleMatcher: any;
}

/* ============ Structured Data helpers ============ */
const generateStructuredData = (
  pageData: any,
  lang: string,
  siteUrl: string,
  navigationData: any
) => {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name:
      process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE === "mxp"
        ? "Milano Malpensa - Aeroporti di Milano"
        : "Milano Linate - Aeroporti di Milano",
    url: `${siteUrl}/${lang}`,
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: pageData?.lblSeoTitle?.[lang],
    description: pageData?.lblSeoDescription?.[lang],
    url: `${siteUrl}${pageData?.lblUrlPath?.[lang]}`,
    isPartOf: {
      "@type": "WebSite",
      name:
        process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE === "mxp"
          ? "Milano Malpensa Airport"
          : "Milano Linate Airport",
      url: `${siteUrl}/${lang}`,
    },
  };

  const breadcrumbItems: any[] = [
    {
      "@type": "ListItem",
      position: 1,
      item: {
        "@id": `${siteUrl}/${lang}`,
        name:
          process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE === "mxp"
            ? "Milano Malpensa"
            : "Milano Linate",
      },
    },
  ];

  if (navigationData && navigationData.length > 0) {
    navigationData.forEach((item: any, index: number) => {
      breadcrumbItems.push({
        "@type": "ListItem",
        position: index + 2,
        item: { "@id": `${siteUrl}${item.url}`, name: item.name },
      });
    });
  }

  if (pageData && pageData.lblUrlPath?.[lang] !== `/${lang}`) {
    breadcrumbItems.push({
      "@type": "ListItem",
      position: breadcrumbItems.length + 1,
      item: {
        "@id": `${siteUrl}${pageData.lblUrlPath?.[lang]}`,
        name:
          pageData.lblSeoTitle?.[lang] ||
          pageData.lblTitle?.[lang] ||
          "Pagina corrente",
      },
    });
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbItems,
  };

  return { organizationSchema, webPageSchema, breadcrumbSchema };
};


function Ads({ lang, isMxp, isVoli }: { lang: string; isMxp: boolean; isVoli: boolean }) {
  const siteId = isMxp ? 299227 : 299226;
  const pageId = isVoli ? (isMxp ? 1095954 : 1095951) : (isMxp ? 1095952 : 1095949);


  const sasInitScript = `
    (function () {
      var w = window;
      var sas = w.sas = w.sas || {};
      sas.cmd = sas.cmd || [];

      // Evita di richiamare più volte sas.setup (fix Chrome/Firefox)
      if (!w.__sasSetupDone) {
        sas.cmd.push(function() {
          sas.setup({
            networkid: 2661,
            domain: 'https://www14.smartadserver.com',
            async: true
          });
        });
        w.__sasSetupDone = true;
      }

      sas.cmd.push(function() {
        sas.call("onecall", {
          siteId: ${siteId},
          pageId: ${pageId},
          formats: [{ id: 56461 }, { id: 56463 }],
          target: 'lang=${lang}'
        });
      });
    })();
  `;

  return (
    <>
 
      <Script
        id="sas-smart-js"
        src="https://ced.sascdn.com/tag/2661/smart.js"
        strategy="beforeInteractive"
      />


      <Script
        id={`sas-init-${pageId}`}
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: sasInitScript }}
      />

      <div id="sas_56461" />
      <Script id={`sas-render-56461-${pageId}`} strategy="afterInteractive">
        {`
          var sas = window.sas = window.sas || {};
          sas.cmd = sas.cmd || [];
          sas.cmd.push(function(){ sas.render("56461"); });
        `}
      </Script>

      <div id="sas_56463" />
      <Script id={`sas-render-56463-${pageId}`} strategy="afterInteractive">
        {`
          var sas = window.sas = window.sas || {};
          sas.cmd = sas.cmd || [];
          sas.cmd.push(function(){ sas.render("56463"); });
        `}
      </Script>
    </>
  );
}

/* ===================== Page ===================== */
const PageComponent: NextPage<IPageProps> = ({
  data,
  paths,
  lang,
  navigationData,
  dataPassToModuleMatcher,
}) => {
  const { i18n } = useTranslation();

  const pageData = data?.data?.find((item) =>
    item.xtaxAirports.ti.find(
      (airportItem) =>
        airportItem.label.toLowerCase() ===
        process.env.NEXT_PUBLIC_AIRPORT_REFERENCE?.toLowerCase()
    )
  );

  // ===== UI breadcrumb: set degli atom =====
  const setPagePath = useSetRecoilState(PagePathAtom);
  const setCrumbsItem = useSetRecoilState(CrumbsItemAtom);
  const setHideCrumbs = useSetRecoilState(HideCrumbsAtom);

  const pagePath = pageData?.xtaxNavigationMenu?.ti?.[0]?.path || "";

  useEffect(() => {
    setPagePath(pagePath);
    setCrumbsItem(navigationData);
    // fallback a false per sicurezza se undefined
    setHideCrumbs(pageData?.flgHideNavigationéath ? pageData.flgHideNavigationéath : false);
  }, [pageData, pagePath, navigationData, setPagePath, setCrumbsItem, setHideCrumbs]);

  // ===== esclusioni moduli (invariato) =====
  const moduleToExcludes = useMemo(() => {
    let excludedModules: string[] = [];
    if (
      pageData &&
      pageData.enuPageCategory === PAGE_CAT.Voli &&
      !(
        FLIGHT_DETAIL_PAGE_URLS[MovementType.D][lang] === pageData.lblUrlPath[lang] ||
        FLIGHT_DETAIL_PAGE_URLS[MovementType.A][lang] === pageData.lblUrlPath[lang]
      )
    ) {
      excludedModules = ["m05"];
    }
    if (pageData && pageData.enuPageCategory === PAGE_CAT.Destinazione) {
      excludedModules = ["m10", "m07", "m05"];
    }
    if (pageData && pageData.enuPageCategory === PAGE_CAT.Servizi) {
      excludedModules = ["m05", "m07", "m10"];
    }
    if (
      pageData &&
      pageData.lblUrlPath[lang]?.toLowerCase() === AIRLINES_LIST_PAGE_URLS[lang]?.toLowerCase()
    ) {
      excludedModules = ["m10", "m05"];
    }
    return excludedModules;
  }, [pageData, lang]);

  useEffect(() => {
    i18n.changeLanguage(lang?.toLowerCase());
  }, [lang, i18n]);

  const siteMxp = process.env.NEXT_PUBLIC_SITE_MALPENSA;
  const siteLin = process.env.NEXT_PUBLIC_SITE_LINATE;
  const airportRef = process.env.NEXT_PUBLIC_IATA_AIRPORT_REFERENCE;
  const siteUrl = (airportRef === "mxp" ? siteMxp : siteLin) || "";

  const isVoli = pageData?.enuPageCategory === PAGE_CAT.Voli;
  const isMxp = airportRef === "mxp";

  const sd = generateStructuredData(pageData, lang, siteUrl, navigationData);

  // Failsafe: se accidentalmente la pagina è /{lang}, emetti anche Org+WebPage
  const isHome =
    pageData?.lblUrlPath?.[lang] &&
    pageData.lblUrlPath[lang].replace(/\/$/, "") === `/${lang}`;

  const jsonLdPayload = isHome
    ? [sd.organizationSchema, sd.webPageSchema, sd.breadcrumbSchema]
    : sd.breadcrumbSchema;

  return (
    <>
      <Head>
        <title>{pageData?.lblSeoTitle?.[lang] || "Milano Airport"}</title>
        <meta name="title" content={pageData?.lblSeoTitle?.[lang] || "Milano Airport"} />
        <meta name="description" content={pageData?.lblSeoDescription?.[lang] || ""} />
        <link rel="canonical" href={`${siteUrl}${pageData?.lblUrlPath?.[lang] || ""}`} />

        {/* Hreflang */}
        {pageData?.lblUrlPath && (
          <>
            <link
              rel="alternate"
              hrefLang="it"
              href={`${siteUrl}${(pageData?.lblUrlPath?.it || pageData?.lblUrlPath?.["it"] || "").replace(/\/$/, "")}`}
            />
            <link
              rel="alternate"
              hrefLang="en"
              href={`${siteUrl}${(pageData?.lblUrlPath?.en || pageData?.lblUrlPath?.["en"] || "").replace(/\/$/, "")}`}
            />
          </>
        )}

        {/* JSON-LD: interno => solo BreadcrumbList; se /{lang} allora anche Org+WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdPayload) }}
        />
      </Head>

      <main>
        {/* Ads */}
        <Ads lang={lang} isMxp={isMxp} isVoli={!!isVoli} />

        {/* News */}
        {pageData?.metainfo?.name?.toLowerCase() === "news" ? (
          <NewsDetails language={lang} data={pageData} />
        ) : null}

        {/* Moduli generici */}
        <ModuleMatcher2
          lang={lang}
          excludes={moduleToExcludes}
          moduleData={dataPassToModuleMatcher}
        />

        {/* Voli - Liste */}
        {pageData &&
        pageData.enuPageCategory === PAGE_CAT.Voli &&
        (FLIGHT_LIST_PAGE_URLS[MovementType.D][lang] === pageData.lblUrlPath[lang] ||
          FLIGHT_LIST_PAGE_URLS[MovementType.A][lang] === pageData.lblUrlPath[lang]) ? (
          <Flights
            language={lang}
            dividerTargetUrl={
              pageData?.lstModule.find((item) => item.xconModules.linkedcontenttype === "m05")
                ?.xconModules.targeturl || ""
            }
          />
        ) : null}

        {/* Servizi */}
        {pageData && pageData.enuPageCategory === PAGE_CAT.Servizi ? (
          <Services language={lang}>
            <ModuleMatcher2
              lang={lang}
              excludes={["m18", "m11"]}
              moduleData={dataPassToModuleMatcher}
            />
          </Services>
        ) : null}

        {/* Destinazioni */}
        {pageData && pageData.enuPageCategory === PAGE_CAT.Destinazione ? (
          <FlightsByDestination
            language={lang}
            dividerTargetUrl={
              pageData?.lstModule.find((item) => item.xconModules.linkedcontenttype === "m05")
                ?.xconModules.targeturl || ""
            }
          >
            <ModuleMatcher2
              lang={lang}
              excludes={["m18", "m05", "m11"]}
              moduleData={dataPassToModuleMatcher}
            />
          </FlightsByDestination>
        ) : null}

        {/* Compagnie aeree */}
        {pageData &&
        pageData.lblUrlPath[lang]?.toLowerCase() ===
          AIRLINES_LIST_PAGE_URLS[lang]?.toLowerCase() ? (
          <AirlineList
            language={lang}
            dividerTargetUrl={
              pageData?.lstModule.find((item) => item.xconModules.linkedcontenttype === "m05")
                ?.xconModules.targeturl || ""
            }
          >
            <ModuleMatcher2
              lang={lang}
              excludes={["m18", "m05"]}
              moduleData={dataPassToModuleMatcher}
            />
          </AirlineList>
        ) : null}
      </main>
    </>
  );
};

export default PageComponent;
